import type {CSSProperties} from 'react';
import {WEDGE_SUBSCRIPTION as S} from '@territory/tokens';
import {wedgeExactRole} from './wedgeRoles.js';

export type WedgeSubscriptionManageLook = 'subscription-cancel' | 'subscription-cancel-busy' | 'subscription-support';

const LOOKS = new Set<string>(['subscription-cancel', 'subscription-cancel-busy', 'subscription-support']);
export const isWedgeSubscriptionManageLook = (look: string | undefined): look is WedgeSubscriptionManageLook => look !== undefined && LOOKS.has(look);

/* No look here draws the transparent target. The busy look is the disabled block: a 1440 wide browser check found that the anchor (a relative
   position and an isolated stacking context) moves the raster of the dimmed block by 1080 pixels. A second browser check found that the target on
   the other two looks changes the tap area against legacy: the Email support pill grows from 36.8px to 46px tall, the rounded corners of Cancel in
   Stripe portal become tappable, and the raster changed beyond noise in 14 of about 570 runs, 13 of them gone with the anchor off. */
export const isWedgeSubscriptionManageNoHitLook = (look: string): boolean => isWedgeSubscriptionManageLook(look);

/* Released cancel sheet of the Subscription screen. Cancel in Stripe portal is a full-width block 50px tall with a 14px radius, the red role as its
   fill, the fixed white subscription ink, no border, 16px semibold type in the Wedge font and the pointer cursor. While the portal call runs the
   released button was dimmed to 0.7 and took the default cursor, so the busy look carries those two declarations. The button is 50px tall, so
   the 44px transparent target sits inside its own box and overlaps no neighbor.
   Email support to cancel is an anchor pill with 9px by 14px of padding, a full radius, the label role as its fill, the page role as its ink, bold 13px
   type in the Wedge font and no underline. It declared no cursor, so it keeps the browser link cursor. The pill is about 35px tall inside an alert box with 12px
   of padding and a 10px gap above, so the 5px the target adds above and below stays inside the box. Both read the exact legacy roles and keep
   the declaration order of the source. The caller owns the label, the handler and the href. */
const CANCEL_HEIGHT = 50, CANCEL_RADIUS = 14, CANCEL_FONT = 16, CANCEL_WEIGHT = 600, BUSY_OPACITY = 0.7;
const SUPPORT_PADDING = '9px 14px', SUPPORT_RADIUS = 999, SUPPORT_FONT = 13, SUPPORT_WEIGHT = 700;

export function wedgeSubscriptionManageStyle(look: WedgeSubscriptionManageLook): CSSProperties {
  const r = wedgeExactRole;
  if (look === 'subscription-support') {
    return {
      display: 'inline-block', padding: SUPPORT_PADDING, borderRadius: SUPPORT_RADIUS, background: r('label'), color: r('bg'),
      fontWeight: SUPPORT_WEIGHT, fontSize: SUPPORT_FONT, textDecoration: 'none', fontFamily: r('font'),
    };
  }
  const busy = look === 'subscription-cancel-busy';
  return {
    width: '100%', height: CANCEL_HEIGHT, borderRadius: CANCEL_RADIUS, background: r('red'), color: S.ink, border: 0,
    cursor: busy ? 'default' : 'pointer', opacity: busy ? BUSY_OPACITY : 1,
    fontSize: CANCEL_FONT, fontWeight: CANCEL_WEIGHT, fontFamily: r('font'),
  };
}
